import { motionLabel, typeLabel, type UnitNotification } from "./unitNotifications";
import { useCopyLocation } from "../../hooks/useCopyLocation";
import { locationLink } from "../../utils/locationLink";
import { useVehiclesSocketStore } from "../../store/admin/vehiclesSocketStore";
import { notificationTitle } from "./unitNotifications";

const fullDate = (ms: number | null) =>
  ms
    ? new Date(ms).toLocaleString("es-MX", { weekday: "short", day: "numeric", month: "short", hour: "numeric", minute: "2-digit", second: "2-digit" })
    : "Sin fecha";

/**
 * El detalle de una notificación desplegado en la lista, como el de Mapon:
 * qué, cuándo (a la hora exacta), quién manejaba, a qué velocidad y dónde.
 * Conductor y velocidad son del momento del aviso (V5-realtime#25): las
 * anteriores no los traen y esas filas no se pintan.
 * shortcut: «Dónde» son coordenadas con enlace, no la calle; si se pide la
 * dirección escrita, resolverla al desplegar con la caché de geocoding.
 */
export default function NotificationDetail({
  n,
  isDarkMode,
  onMap,
  showUnit = false,
  inset = "pl-[38px]",
}: {
  n: UnitNotification;
  isDarkMode: boolean;
  onMap: () => void;
  /** En la campana general, que mezcla toda la flota. */
  showUnit?: boolean;
  /** Sangría del texto para que quede bajo el título y no bajo el ícono de la fila. */
  inset?: string;
}) {
  const { copy, state, available } = useCopyLocation(n.lat, n.lon);
  // Geocercas y viajes pueden llegar sin vehicle_name (V5-realtime lo saca de
  // su caché y a veces no está): se toma de la lista antes que mostrar el id.
  const listName = useVehiclesSocketStore((s) => (showUnit && n.device_id ? s.vehicles.find((v) => v.id === n.device_id)?.vehicle?.name : undefined));
  const link = locationLink(n.lat, n.lon);
  const muted = isDarkMode ? "text-gray-400" : "text-gray-500";
  const motion = motionLabel(n);
  const btn = `flex-1 flex items-center justify-center gap-1 whitespace-nowrap rounded-md border px-2 py-1.5 text-xs transition-[background-color,transform] duration-150 active:scale-[0.97] ${
    isDarkMode ? "border-dark-border hover:bg-white/5" : "border-gray-200 hover:bg-white"
  }`;
  return (
    <div className="px-3 pb-3 text-xs animate-detailIn motion-reduce:animate-none">
      <div className={inset}>
      {typeLabel(n.type).toLowerCase() !== notificationTitle(n).toLowerCase() && (
        <p className={`text-[10px] uppercase tracking-wide ${muted}`}>{typeLabel(n.type)}</p>
      )}
      {n.description && n.description !== n.title && <p className="mt-0.5 leading-snug">{n.description}</p>}
      <dl className="mt-1.5 grid grid-cols-[auto_1fr] gap-x-2 gap-y-1">
        {showUnit && (
          <>
            <dt className={muted}><i className="ri-truck-line" aria-hidden="true" /> Unidad</dt>
            <dd className="font-mono font-semibold truncate">{n.vehicle_name || listName || n.device_id || "Sin unidad"}</dd>
          </>
        )}
        <dt className={muted}><i className="ri-time-line" aria-hidden="true" /> Cuándo</dt>
        <dd className="tabular-nums">{fullDate(n.created_at)}</dd>
        {n.driver_name && (
          <>
            <dt className={muted}><i className="ri-steering-2-line" aria-hidden="true" /> Conductor</dt>
            <dd className="truncate">{n.driver_name}</dd>
          </>
        )}
        {motion && (
          <>
            <dt className={muted}><i className="ri-speed-up-line" aria-hidden="true" /> Velocidad</dt>
            <dd className="tabular-nums">{motion}</dd>
          </>
        )}
        <dt className={muted}><i className="ri-map-pin-line" aria-hidden="true" /> Dónde</dt>
        <dd className="tabular-nums">
          {link ? (
            <a href={link} target="_blank" rel="noreferrer" className="text-primary-500 hover:underline">
              {Number(n.lat).toFixed(5)}, {Number(n.lon).toFixed(5)}
            </a>
          ) : (
            "Sin ubicación"
          )}
        </dd>
      </dl>
      </div>
      <div className="mt-2.5 flex gap-1.5">
        <button type="button" onClick={onMap} disabled={!n.device_id} className={`${btn} disabled:opacity-40`}>
          <i className="ri-focus-3-line" aria-hidden="true" /> Ver en el mapa
        </button>
        {available && (
          <button type="button" onClick={copy} className={btn}>
            <i className={state === "copied" ? "ri-check-line text-green-500" : "ri-file-copy-line"} aria-hidden="true" />
            {state === "copied" ? "Copiada" : state === "failed" ? "No se pudo" : "Copiar ubicación"}
          </button>
        )}
      </div>
    </div>
  );
}
